// Turns the raw "dependencies" detected server-side (AI/API keyword matches, STT/TTS function calls)
// into the {provider|vendor, model, block} shape the bot profile cards expect.

const AI_PROVIDERS = {
    openai: { provider: "OpenAI" },
    chatgpt: { provider: "OpenAI", model: "ChatGPT" },
    gpt: { provider: "OpenAI", model: "GPT" },
    anthropic: { provider: "Anthropic" },
    claude: { provider: "Anthropic", model: "Claude" },
    gemini: { provider: "Google", model: "Gemini" },
    bard: { provider: "Google", model: "Bard" },
    mistral: { provider: "Mistral AI" },
    togetherai: { provider: "Together AI" },
    "together.ai": { provider: "Together AI" },
    huggingface: { provider: "Hugging Face" },
    cohere: { provider: "Cohere" },
    perplexity: { provider: "Perplexity" },
    groq: { provider: "Groq" },
    llama: { provider: "Meta", model: "Llama" },
};

const VENDOR_LABELS = {
    deepgram: "Deepgram",
    elevenlabs: "ElevenLabs",
    cartesia: "Cartesia",
    gradium: "Gradium",
    google: "Google",
    uhlive: "UHLive",
    gladia: "Gladia",
};

function truncate(text, max = 70) {
    if (!text) return "";
    return text.length > max ? `${text.slice(0, max)}...` : text;
}

// Mirrors the /company/:company/bots/:bot#object_id pattern used by ConversationPath and the home search
function blockEditorUrl(bot, block) {
    if (!bot.company || !bot.company.name || !block.object_id) return null;
    return `${window.location.protocol}//${window.location.host}/company/${encodeURIComponent(bot.company.name)}/bots/${encodeURIComponent(bot.name)}#${block.object_id}`;
}

function toBlockBadge(bot, block) {
    if (!block) return null;
    return { name: block.name, color: block.color, href: blockEditorUrl(bot, block) };
}

// The backend only matches a coarse keyword (e.g. "gpt-4"), but the raw url/value often carries
// the precise model (e.g. "gpt-4.1", "gpt-4o-mini") right after it. Extend the match to capture it.
function extractModelDetail(raw, keyword) {
    if (!raw || !keyword) return null;

    const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const match = raw.match(new RegExp(`${escapedKeyword}[\\w.-]*`, "i"));
    return match ? match[0] : null;
}

function mapAiDependency(bot, dependency) {
    const known = AI_PROVIDERS[dependency.keyword] || { provider: dependency.keyword };
    const raw = dependency.url || dependency.value;
    const detail = extractModelDetail(raw, dependency.keyword);
    const hasPreciseDetail = detail && detail.toLowerCase() !== dependency.keyword.toLowerCase();

    return {
        provider: known.provider,
        model: (hasPreciseDetail ? detail : null) || known.model || truncate(raw),
        block: toBlockBadge(bot, dependency.block),
    };
}

function mapVoiceDependency(bot, dependency) {
    return {
        vendor: VENDOR_LABELS[dependency.vendor] || dependency.vendor,
        model: dependency.function,
        block: toBlockBadge(bot, dependency.block),
    };
}

// Groups external API calls by domain, so 20 calls to the same host show as one row with all its blocks
function groupExternalApisByHost(bot, externalApis) {
    const groups = new Map();

    externalApis.forEach((dependency) => {
        if (!groups.has(dependency.host)) {
            groups.set(dependency.host, { host: dependency.host, blocks: [] });
        }

        const badge = toBlockBadge(bot, dependency.block);
        if (badge) {
            groups.get(dependency.host).blocks.push(badge);
        }
    });

    return Array.from(groups.values());
}

function normalizeBotProfile(bot) {
    const dependencies = bot.dependencies || {};

    return {
        id: bot.id,
        name: bot.name,
        ai: (dependencies.ai || []).map((dependency) => mapAiDependency(bot, dependency)),
        tts: (dependencies.tts || []).map((dependency) => mapVoiceDependency(bot, dependency)),
        stt: (dependencies.stt || []).map((dependency) => mapVoiceDependency(bot, dependency)),
        externalApis: groupExternalApisByHost(bot, dependencies.external_apis || []),
    };
}

export default normalizeBotProfile;
